import { z } from 'zod';

import { HotelCancellationPolicySchema } from './hotel-cancellation-policy.js';
import { TravelCancellationSchema } from './travel-cancellation.js';

/**
 * Standalone hotel booking: everything after the traveller has picked a room.
 *
 * Search / rooms live in `tbo-hotel.ts` — they are read-only price discovery.
 * This file covers the mutating half of the funnel described in
 * `docs/architecture/TBO-STANDALONE-BOOKING-PLAN.md` §5.2:
 *
 *   Search → PreBook → session → guests → pay → Book → BookingDetail
 *
 * **Which TBO hotel API this models.** The TekTravels *Universal* Hotel API
 * (TokenId + TraceId, `BlockRoom`, `IsVouchered`, `GenerateVoucher`) is NOT what
 * this platform is wired to. `TboHotelApiClient` authenticates with HTTP Basic
 * against the TBO Holidays HotelAPI, where the funnel is `Search` → `PreBook` →
 * `Book` and the carry-forward token is a `BookingCode`, not a TraceId. The two
 * flows do not translate; do not port `BlockRoom` semantics onto these types.
 *
 * Three invariants shape everything below, all inherited from the flight funnel
 * because the failure modes are identical:
 *
 * 1. **The client never sends a `bookingCode` after the session is created.** It
 *    lives server-side on the booking row. A client that could name a booking
 *    code could book a rate it was never quoted, at a price it chose.
 * 2. **Money is a numeric string of minor units (paise).** JSON has no BigInt,
 *    and a `number` loses precision past 2^53 — reachable in minor units for a
 *    multi-room long stay.
 * 3. **The cancellation policy is a snapshot, not a live read.** What is stored
 *    against the booking is what the guest agreed to, and it is the only
 *    defensible record of that at cancellation time.
 */

// ---- Occupancy ----------------------------------------------------------

/**
 * One room's occupancy. Mirrors TBO's `PaxRooms[]` entry, and the array index
 * is load-bearing: `guestInRoom` on every guest addresses it, and the room
 * options returned by search are combination offers whose sub-rooms line up
 * with it positionally.
 */
export const HotelRoomOccupancyInputSchema = z.object({
  adults: z.number().int().min(1).max(9),
  children: z.number().int().min(0).max(4).default(0),
  /**
   * One age per child, 0–17. TBO rejects the search outright when the count
   * disagrees with `children`, and the ages drive extra-bed and child-rate
   * rules — so they are carried unchanged from search all the way to Book.
   */
  childAges: z.array(z.number().int().min(0).max(17)).max(4).default([]),
});
export type HotelRoomOccupancyInput = z.infer<typeof HotelRoomOccupancyInputSchema>;

// ---- Guests -------------------------------------------------------------

export const HotelGuestTypeSchema = z.enum(['ADULT', 'CHILD']);
export type HotelGuestType = z.infer<typeof HotelGuestTypeSchema>;

export const HotelGuestTitleSchema = z.enum(['Mr', 'Mrs', 'Ms', 'Miss', 'Mstr', 'Dr']);
export type HotelGuestTitle = z.infer<typeof HotelGuestTitleSchema>;

/**
 * Hotel name rules. Looser than the airline ones — there is no machine-readable
 * zone to match — but digits and punctuation still fail at several suppliers,
 * and they fail at `Book`, i.e. after the money is taken.
 */
const guestNameSchema = z
  .string()
  .trim()
  .min(2, 'Must be at least 2 characters')
  .max(60)
  .regex(/^[A-Za-z][A-Za-z\s'.-]*$/, 'Use letters, spaces, hyphens and apostrophes only');

export const HotelGuestInputSchema = z.object({
  guestType: HotelGuestTypeSchema,
  title: HotelGuestTitleSchema,
  firstName: guestNameSchema,
  lastName: guestNameSchema,
  /**
   * Which room this guest occupies, **1-indexed** to match TBO's own
   * convention. Validated against the occupancy that was searched.
   */
  guestInRoom: z.number().int().min(1).max(8),
  /**
   * Exactly one per room. The lead guest is who the property asks for at
   * check-in and who the supplier addresses the reservation to.
   */
  isLeadGuest: z.boolean().default(false),
  /** Mandatory for children — it must equal the age sent at search. */
  age: z.number().int().min(0).max(120).nullable().default(null),
  /** Indian suppliers ask for this on domestic stays for the lead guest. */
  panNumber: z
    .string()
    .trim()
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, 'Invalid PAN')
    .nullable()
    .default(null),
  passportNumber: z.string().trim().min(5).max(20).nullable().default(null),
  /**
   * `YYYY-MM-DD`. TBO takes the expiry with the number, not the number alone,
   * so a property that demands a passport demands both.
   */
  passportExpiry: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .nullable()
    .default(null),
});
export type HotelGuestInput = z.infer<typeof HotelGuestInputSchema>;

/** Contact block — captured once per booking, not per guest. */
export const HotelContactInputSchema = z.object({
  email: z.string().trim().email().max(120),
  /** Country dialling code without the `+`, e.g. "91". */
  phoneCountryCode: z
    .string()
    .trim()
    .regex(/^\d{1,4}$/, 'Digits only'),
  phone: z
    .string()
    .trim()
    .regex(/^\d{6,14}$/, 'Digits only'),
  addressLine: z.string().trim().max(200).nullable().default(null),
  city: z.string().trim().max(80).nullable().default(null),
  /**
   * The guest's state, for the tax invoice.
   *
   * Not part of the address and not tied to the GST block: under Indian GST the
   * *place of supply* decides whether the invoice carries IGST or CGST+SGST, and
   * that is answered by the customer's state whether or not they claim input
   * credit. Asked once, on the booking, rather than only from the minority who
   * fill in a GSTIN.
   */
  state: z.string().trim().max(60).nullable().default(null),
  /** ISO-3166-1 alpha-2. */
  countryCode: z.string().trim().length(2).default('IN'),
});
export type HotelContactInput = z.infer<typeof HotelContactInputSchema>;

/** Optional GST block for Indian business bookings. */
export const HotelGstInputSchema = z.object({
  gstNumber: z
    .string()
    .trim()
    .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]{3}$/, 'Invalid GSTIN'),
  companyName: z.string().trim().min(2).max(120),
  companyAddress: z.string().trim().max(200).nullable().default(null),
  companyEmail: z.string().trim().email().max(120).nullable().default(null),
  companyPhone: z.string().trim().max(20).nullable().default(null),
});
export type HotelGstInput = z.infer<typeof HotelGstInputSchema>;

export const SaveHotelGuestsInputSchema = z.object({
  guests: z.array(HotelGuestInputSchema).min(1).max(40),
  contact: HotelContactInputSchema,
  gst: HotelGstInputSchema.nullable().default(null),
  /** Free text forwarded to the property. Never a guarantee of anything. */
  specialRequests: z.string().trim().max(500).nullable().default(null),
});
export type SaveHotelGuestsInput = z.infer<typeof SaveHotelGuestsInputSchema>;

// ---- Cancellation policy ------------------------------------------------

/**
 * Re-exported from the leaf module that owns them. They live there so that
 * `travel-cancellation.ts` (which this file imports) can reference a policy band
 * without the two modules forming an import cycle.
 */
export {
  HotelCancellationBandSchema,
  HotelCancellationPolicySchema,
  type HotelCancellationBand,
  type HotelCancellationPolicy,
} from './hotel-cancellation-policy.js';

// ---- Session creation ---------------------------------------------------

/**
 * Open a booking session for one selected rate.
 *
 * This is the last request that carries a `bookingCode`. The server PreBooks it
 * immediately — that call is both the revalidation gate and the point at which
 * the cancellation policy is pinned — and from here on the browser names only a
 * `sessionId`.
 *
 * The stay criteria are re-sent rather than looked up because the rate is only
 * meaningful against the exact dates, occupancy and nationality it was quoted
 * for. The server re-validates that the code prices under them.
 */
export const CreateHotelBookingSessionInputSchema = z.object({
  hotelCode: z.string().trim().min(1).max(64),
  /** TBO `BookingCode` for the chosen rate, from the detailed room search. */
  bookingCode: z.string().trim().min(1).max(512),
  checkIn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
  checkOut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
  rooms: z.array(HotelRoomOccupancyInputSchema).min(1).max(8),
  /**
   * ISO-2. **Materially changes price and availability** — it is a search
   * input, not a formality, and it must not be hardcoded anywhere in the funnel.
   */
  guestNationality: z.string().trim().length(2).default('IN'),
  currencyCode: z.string().trim().length(3).default('INR'),
  /**
   * The destination this rate was found in, carried purely so the booking can
   * be linked back to later.
   *
   * Nothing in pricing or booking reads it — a rate is identified by its
   * `bookingCode` alone. It exists because the recovery email for an abandoned
   * booking has to offer a way *forward*, and by the time it is sent the rate
   * hold has long lapsed: the only honest link is back to the search that found
   * it, and a hotel search cannot be reconstructed without the city. Everything
   * else it needs (dates, occupancy, nationality, currency) is already on the
   * booking.
   *
   * Optional, so a client that does not send it still books normally — the
   * recovery email simply falls back to a plainer link.
   */
  cityId: z.string().trim().max(64).optional(),
  cityName: z.string().trim().max(160).optional(),
});
export type CreateHotelBookingSessionInput = z.infer<typeof CreateHotelBookingSessionInputSchema>;

// ---- Pricing breakdown --------------------------------------------------

export const HotelPriceBreakdownSchema = z.object({
  /** Supplier room rate, converted to the sell currency. */
  basePaise: z.string().regex(/^-?\d+$/),
  /** Supplier taxes and fees. */
  taxPaise: z.string().regex(/^-?\d+$/),
  markupPaise: z.string().regex(/^-?\d+$/),
  convenienceFeePaise: z.string().regex(/^-?\d+$/),
  gstPaise: z.string().regex(/^-?\d+$/),
  /**
   * Rounding to the whole unit, signed, so the rows still sum to `totalPaise`.
   * Derived from the stored booking; absent (older API) reads as 0.
   */
  roundOffPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  /** What the guest is charged. The only figure sent to the gateway. */
  totalPaise: z.string().regex(/^-?\d+$/),
  currencyCode: z.string(),
  /** Nights × rooms, for "₹X per room per night" lines. */
  nights: z.number().int().min(1),
  roomCount: z.number().int().min(1),
});
export type HotelPriceBreakdown = z.infer<typeof HotelPriceBreakdownSchema>;

// ---- Session view -------------------------------------------------------

/**
 * Reuses the shared `TravelBookingStatus` values. Hotels have no separate
 * "pending at supplier" state: a `Book` that answers `Pending` stays in
 * `BOOKING`, which is already defined as "outcome not yet known" and which the
 * terminal page already renders as *confirming*, never as failed.
 */
export const HotelBookingStatusSchema = z.enum([
  'DRAFT',
  'PRICED',
  'PAX_ENTERED',
  'PAYMENT_PENDING',
  'PAID',
  'BOOKING',
  /**
   * Paid, and parked: the supplier refused for a reason that is ours to fix
   * (an agency wallet, a credential), so the money is kept and an operator
   * either re-issues or releases the hold. Neutral name on purpose - it
   * reaches the guest's status page. See the flight twin.
   */
  'ON_HOLD',
  'CONFIRMED',
  'BOOK_FAILED',
  /**
   * Raised with TBO, no terminal answer yet. Distinct from `CANCELLED` because
   * the supplier can still refuse, and a booking marked cancelled on the
   * strength of the request being *accepted* is a guest told their room is gone
   * while the reservation is still live.
   */
  'CANCELLATION_REQUESTED',
  'CANCELLED',
  'REFUNDED',
  'EXPIRED',
  /**
   * Mirrors the shared Prisma enum. Flight-only in practice (a locked non-LCC
   * fare); a hotel booking never reaches it, but the value is listed so the
   * DB-to-view mapping stays total.
   */
  'HELD',
]);
export type HotelBookingStatus = z.infer<typeof HotelBookingStatusSchema>;

/** One room as it was booked, for the voucher. */
export const HotelBookedRoomSchema = z.object({
  /** 1-indexed, matching `guestInRoom`. */
  roomNumber: z.number().int().min(1),
  name: z.string(),
  /** Board basis: "Room Only", "Breakfast", … */
  mealType: z.string().nullable(),
  adults: z.number().int(),
  children: z.number().int(),
  childAges: z.array(z.number().int()).default([]),
  guests: z
    .array(
      z.object({
        title: z.string().nullable(),
        firstName: z.string(),
        lastName: z.string(),
        guestType: HotelGuestTypeSchema,
        isLeadGuest: z.boolean(),
      }),
    )
    .default([]),
});
export type HotelBookedRoom = z.infer<typeof HotelBookedRoomSchema>;

export const HotelBookingSessionSchema = z.object({
  sessionId: z.string(),
  reference: z.string(),
  status: HotelBookingStatusSchema,

  hotelCode: z.string(),
  hotelName: z.string(),
  starRating: z.number().int().nullable(),
  address: z.string().nullable(),
  coverPhotoUrl: z.string().nullable(),
  /** Property check-in / check-out clock times, when the supplier publishes them. */
  checkInTime: z.string().nullable().default(null),
  checkOutTime: z.string().nullable().default(null),

  checkIn: z.string(),
  checkOut: z.string(),
  nights: z.number().int().min(1),
  guestNationality: z.string(),
  rooms: z.array(HotelBookedRoomSchema),

  price: HotelPriceBreakdownSchema,
  cancellationPolicy: HotelCancellationPolicySchema,
  /** What the rate covers, as the supplier words it. */
  inclusion: z.string().nullable().default(null),

  /** ISO timestamp the price lock lapses at. Drives the countdown. */
  priceExpiresAt: z.string().nullable(),
  /**
   * The contact block saved on this session, in the shape the guest save
   * accepts, or null until the guest step has been saved once. The names
   * themselves ride on `rooms[].guests`. Lets a session reopened in a fresh
   * tab - "Continue your booking", a reminder link - refill the form from the
   * server instead of asking again. Defaulted for cached sessions.
   */
  savedContact: HotelContactInputSchema.nullable().default(null),
  /**
   * The guest rows saved on this session, in the shape the guest save accepts
   * - documents included, which `rooms[].guests` deliberately omits. A form
   * refilled from the names alone and saved again would post blank documents
   * over the ones on the server. Empty until the guest step has been saved
   * once, and while the booking is no longer being made.
   */
  savedGuests: z.array(HotelGuestInputSchema).default([]),
  /**
   * While `ON_HOLD`: when the hold is released automatically if nobody acts.
   * Null in every other status. See the flight twin.
   */
  holdExpiresAt: z.string().nullable().default(null),
  /**
   * A gateway order has been minted for this booking.
   *
   * The confirmation page needs to tell two `PAYMENT_PENDING` bookings apart:
   * one nobody ever tried to pay for, and one where the checkout was opened and
   * the capture simply has not been reported back yet. Only the first may be
   * offered a "Back to payment" button — showing it on the second puts a
   * pay-again control in front of someone whose money has already left their
   * account.
   *
   * The page used to answer that question from a browser-local stash written at
   * checkout, which is correct on the device that paid and wrong everywhere
   * else. This flag is on the booking itself, so every device gets the same
   * answer. See the identical field on `FlightBookingSessionSchema`.
   */
  paymentInitiated: z.boolean().default(false),
  /**
   * True when the last PreBook came back at a different net rate. The guest has
   * to re-consent; the funnel must not move on until they have.
   */
  isPriceChanged: z.boolean().default(false),
  /**
   * True when the last PreBook came back with different cancellation terms.
   * Handled separately from price on purpose — a policy that hardens at an
   * unchanged price is the more dangerous of the two and is invisible if the
   * two flags are collapsed into one.
   */
  isCancellationPolicyChanged: z.boolean().default(false),

  /**
   * What this property demands before it will accept the booking, as PreBook's
   * `ValidationInfo` stated it. The guest form asks for the document only when
   * one of these is true — a passport field on every stay is friction, and a
   * missing one on the stays that need it is a refund.
   */
  isPanMandatory: z.boolean().default(false),
  isPassportMandatory: z.boolean().default(false),

  /**
   * Whether the supplier wants every guest named, or just the lead of each room.
   *
   * `RequireAllPaxDetails` from PreBook. False on nearly every rate, and the
   * default when the response omits it — so the guest form asks for one name
   * per room rather than one per head, which is what a guest booking four rooms
   * for a group expects and what every other OTA does.
   *
   * The occupancy in `rooms` is untouched by this and stays the thing the rate
   * was priced against. All the flag decides is how many of those people we
   * hold names for; a room priced for two adults with one named lead is a
   * complete booking, not a half-filled one.
   */
  requireAllGuestDetails: z.boolean().default(false),

  /**
   * The property is outside the point-of-sale country.
   *
   * Separate from `isPassportMandatory`, which is the property's own demand:
   * this is ours. Passport details go on the record for every international
   * stay, because the hotel API — unlike the flight one — never tells us when a
   * property will want them, and a passport nobody captured at booking is a
   * phone call weeks later. The form treats either as "collect the passport",
   * and words the reason differently for each.
   */
  isInternationalStay: z.boolean().default(false),

  /** Set once Book lands. */
  confirmationNo: z.string().nullable(),
  supplierBookingId: z.string().nullable(),
  /** Supplier's own status string, when it says something we don't model. */
  supplierStatus: z.string().nullable().default(null),
  specialRequests: z.string().nullable().default(null),
  failureReason: z.string().nullable(),
  /**
   * Where cancellation stands. Always present — `NOT_APPLICABLE` on a booking
   * that never confirmed — so the page never has to tell "no data" apart from
   * "cannot cancel".
   */
  cancellation: TravelCancellationSchema,
});
export type HotelBookingSession = z.infer<typeof HotelBookingSessionSchema>;

// ---- Payment ------------------------------------------------------------

export const HotelPaymentInitSchema = z.object({
  provider: z.enum(['PAYU', 'RAZORPAY']),
  keyId: z.string(),
  orderId: z.string(),
  amountPaise: z.string().regex(/^-?\d+$/),
  currencyCode: z.string(),
  description: z.string(),
  prefill: z.object({
    name: z.string(),
    email: z.string(),
    contact: z.string(),
  }),
});
export type HotelPaymentInit = z.infer<typeof HotelPaymentInitSchema>;

export const VerifyHotelPaymentInputSchema = z.object({
  orderId: z.string().trim().min(1),
  paymentId: z.string().trim().min(1),
  signature: z.string().trim().min(1),
});
export type VerifyHotelPaymentInput = z.infer<typeof VerifyHotelPaymentInputSchema>;

export const VerifyHotelPaymentResultSchema = z.object({
  acknowledged: z.literal(true),
  /** Replay of an already-settled payload — not an error, just a no-op. */
  alreadyPaid: z.boolean(),
  status: HotelBookingStatusSchema,
});
export type VerifyHotelPaymentResult = z.infer<typeof VerifyHotelPaymentResultSchema>;

// ---- Cancellation -------------------------------------------------------
//
// The hotel-specific quote and result schemas that used to live here were
// replaced by the product-neutral ones in `travel-cancellation.ts`. The band a
// quote reports is still hotel-only; it is carried as an optional field there
// rather than justifying a second schema.
